/**
 * Entity card sizing heuristics and "next free slot" placement in the visible viewport
 * (column layout with overlap avoidance). Used when adding entities from explorer / uploads.
 */

import type { Entity } from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { useCallback } from "react";
import {
  CANVAS_SNAP,
  CANVAS_SPACING,
  snapToGrid,
} from "../canvas-controls.config";
import { useCanvasUi } from "../context/CanvasUiContext";
import {
  DEFAULT_FILE_NODE_HEIGHT,
  DEFAULT_FILE_NODE_WIDTH,
  defaultFileRecordNodeBox,
  getEntityMediaDisplayBox,
} from "../utils/imageDimensions";

/**
 * A generic entity that carries a cover image renders as an embedded thumbnail
 * tile (same shape as a non-media file_record) rather than a card row.
 */
function isGenericImageTile(entity: Entity): boolean {
  return (
    "base" in entity &&
    entity.base === "generic" &&
    !!(entity.data as { image_url?: string })?.image_url
  );
}

function rectsOverlap(
  a: { left: number; top: number; right: number; bottom: number },
  b: { left: number; top: number; right: number; bottom: number },
): boolean {
  return !(
    a.right <= b.left ||
    a.left >= b.right ||
    a.bottom <= b.top ||
    a.top >= b.bottom
  );
}

/**
 * Returns helpers for entity node dimensions and for computing the next
 * placement position on the canvas (viewport-based column/gap algorithm).
 */
export function useCanvasNodePlacement() {
  const { reactFlowWrapperRef } = useCanvasUi();
  const reactFlowInstance = useReactFlow();

  const getEntityNodeHeight = useCallback((entity: Entity): number => {
    const SQUARE_NODE_SIZE = 320;
    const CARD_NODE_HEIGHT = 96;

    const fitted = getEntityMediaDisplayBox(entity);
    if (fitted) {
      return fitted.height;
    }

    const type = (entity as any).type as string | undefined;
    const base = (entity as any).base as string | undefined;

    // file_record tiles: image/video already returned via `fitted` above.
    // Every other file record is a page and reserves the portrait box —
    // match it here so multi-drop placement reserves the right vertical
    // pitch. One shared answer with every creation path.
    if (base === "file_record") {
      const data = (
        entity as { data?: { content_type?: string; origin_type?: string } }
      )?.data;
      return defaultFileRecordNodeBox(data?.content_type, data?.origin_type)
        .height;
    }

    // Generic entities with a cover render as the same 2:1 thumbnail tile.
    if (isGenericImageTile(entity)) {
      return DEFAULT_FILE_NODE_HEIGHT;
    }

    if (
      type === "canvas_image" ||
      type === "canvas_video" ||
      type === "canvas_artifact"
    ) {
      return SQUARE_NODE_SIZE;
    }

    // A skill renders as a compact entity card (SkillCardEntity), not a square
    // media tile — so its placement height must match the real card height,
    // otherwise the cursor-centering reserves 350px and drops it off-cursor.
    if (type === "skill") {
      return CARD_NODE_HEIGHT;
    }

    if (base === "file") {
      return SQUARE_NODE_SIZE;
    }

    return CARD_NODE_HEIGHT;
  }, []);

  const getEntityNodeWidth = useCallback((entity: Entity): number => {
    const fitted = getEntityMediaDisplayBox(entity);
    if (fitted) {
      return fitted.width;
    }
    const base = (entity as any).base as string | undefined;
    if (base === "file_record" || isGenericImageTile(entity)) {
      return DEFAULT_FILE_NODE_WIDTH;
    }
    return DEFAULT_FILE_NODE_WIDTH;
  }, []);

  const getNodeBounds = useCallback(
    (
      node: Node,
    ): { left: number; top: number; right: number; bottom: number } => {
      const entity = node.data?.entity as Entity | undefined;
      const w =
        node.width ??
        (node as any).measured?.width ??
        (entity ? getEntityNodeWidth(entity) : DEFAULT_FILE_NODE_WIDTH);
      const h =
        node.height ??
        (node as any).measured?.height ??
        (entity ? getEntityNodeHeight(entity) : 96);
      return {
        left: node.position.x,
        top: node.position.y,
        right: node.position.x + w,
        bottom: node.position.y + h,
      };
    },
    [getEntityNodeWidth, getEntityNodeHeight],
  );

  const getNextNodePosition = useCallback(
    (newNodeWidth = 320, newNodeHeight = 96): { x: number; y: number } => {
      const viewport = reactFlowInstance.getViewport();
      const flowNodes = reactFlowInstance.getNodes();

      const [gridX, gridY] = CANVAS_SNAP.grid;

      const containerW =
        reactFlowWrapperRef.current?.clientWidth ?? window.innerWidth;
      const containerH =
        reactFlowWrapperRef.current?.clientHeight ?? window.innerHeight;

      const vpLeft = -viewport.x / viewport.zoom;
      const vpTop = -viewport.y / viewport.zoom;
      const vpRight = vpLeft + containerW / viewport.zoom;
      const vpBottom = vpTop + containerH / viewport.zoom;

      const MARGIN = 80;
      const NODE_GAP = CANVAS_SPACING.nodeGap;
      const COLUMN_PITCH = 320 + NODE_GAP;
      const GRID_ORIGIN = 16;

      const startY = vpTop + MARGIN;

      const existingBounds = flowNodes.map((node) => getNodeBounds(node));
      const vpBottomMargin = vpBottom - MARGIN;

      const getColumnBottom = (
        colX: number,
        colWidth: number,
      ): number | null => {
        let max = -Infinity;
        let found = false;
        const colRight = colX + colWidth;
        for (let i = 0; i < flowNodes.length; i++) {
          const b = existingBounds[i];
          if (b.right <= colX || b.left >= colRight) continue;
          if (b.top >= vpBottom || b.bottom <= vpTop) continue;
          if (b.bottom > max) max = b.bottom;
          found = true;
        }
        return found ? max : null;
      };

      const getOccupiedIntervals = (
        colX: number,
        colRight: number,
      ): Array<{ top: number; bottom: number }> => {
        const intervals: Array<{ top: number; bottom: number }> = [];
        for (let i = 0; i < existingBounds.length; i++) {
          const b = existingBounds[i];
          if (b.right <= colX || b.left >= colRight) continue;
          if (b.top >= vpBottom || b.bottom <= vpTop) continue;
          intervals.push({ top: b.top, bottom: b.bottom });
        }
        return intervals;
      };

      const getGaps = (
        intervals: Array<{ top: number; bottom: number }>,
        rangeTop: number,
        rangeBottom: number,
      ): Array<{ top: number; bottom: number }> => {
        if (intervals.length === 0)
          return [{ top: rangeTop, bottom: rangeBottom }];
        const sorted = [...intervals].sort((a, b) => a.top - b.top);
        const merged: Array<{ top: number; bottom: number }> = [];
        let [curTop, curBottom] = [sorted[0].top, sorted[0].bottom];
        for (let i = 1; i < sorted.length; i++) {
          if (sorted[i].top <= curBottom) {
            curBottom = Math.max(curBottom, sorted[i].bottom);
          } else {
            merged.push({ top: curTop, bottom: curBottom });
            [curTop, curBottom] = [sorted[i].top, sorted[i].bottom];
          }
        }
        merged.push({ top: curTop, bottom: curBottom });

        const gaps: Array<{ top: number; bottom: number }> = [];
        let y = rangeTop;
        for (const m of merged) {
          if (m.top > y)
            gaps.push({ top: y, bottom: Math.min(m.top, rangeBottom) });
          y = Math.max(y, m.bottom);
          if (y >= rangeBottom) break;
        }
        if (y < rangeBottom) gaps.push({ top: y, bottom: rangeBottom });
        return gaps.filter((g) => g.bottom > g.top);
      };

      const candidateOverlapsAny = (
        left: number,
        top: number,
        right: number,
        bottom: number,
      ): boolean => {
        const candidate = { left, top, right, bottom };
        return existingBounds.some((b) => rectsOverlap(candidate, b));
      };

      const firstColIdx = Math.ceil((vpLeft - GRID_ORIGIN) / COLUMN_PITCH);
      const lastColIdx = Math.floor(
        (vpRight - MARGIN - newNodeWidth - GRID_ORIGIN) / COLUMN_PITCH,
      );
      const endColIdx = Math.min(lastColIdx, firstColIdx + 9);

      for (let idx = firstColIdx; idx <= endColIdx; idx++) {
        const colX = GRID_ORIGIN + idx * COLUMN_PITCH;
        const colRight = colX + newNodeWidth;

        const intervals = getOccupiedIntervals(colX, colRight);
        const gaps = getGaps(intervals, startY, vpBottomMargin);

        for (const gap of gaps) {
          const gapHeight = gap.bottom - gap.top;
          if (gapHeight < newNodeHeight + NODE_GAP) continue;
          const candidateY = gap.top + NODE_GAP;
          const candidateBottom = candidateY + newNodeHeight;
          if (candidateBottom <= gap.bottom) {
            const snappedX = snapToGrid(colX, gridX);
            const snappedY = snapToGrid(candidateY, gridY);
            const snappedRight = snappedX + newNodeWidth;
            const snappedBottom = snappedY + newNodeHeight;
            if (
              !candidateOverlapsAny(
                snappedX,
                snappedY,
                snappedRight,
                snappedBottom,
              )
            ) {
              return { x: snappedX, y: snappedY };
            }
          }
        }

        const colBottom = getColumnBottom(colX, newNodeWidth);
        if (colBottom !== null) {
          const candidateY = colBottom + NODE_GAP;
          if (candidateY + newNodeHeight <= vpBottomMargin) {
            const snappedX = snapToGrid(colX, gridX);
            const snappedY = snapToGrid(candidateY, gridY);
            const snappedRight = snappedX + newNodeWidth;
            const snappedBottom = snappedY + newNodeHeight;
            if (
              !candidateOverlapsAny(
                snappedX,
                snappedY,
                snappedRight,
                snappedBottom,
              )
            ) {
              return { x: snappedX, y: snappedY };
            }
          }
        }
      }

      const fallbackX = GRID_ORIGIN + firstColIdx * COLUMN_PITCH;
      return {
        x: snapToGrid(fallbackX, gridX),
        y: snapToGrid(startY + NODE_GAP * 2, gridY),
      };
    },
    [reactFlowInstance, reactFlowWrapperRef, getNodeBounds],
  );

  return {
    getEntityNodeHeight,
    getEntityNodeWidth,
    getNodeBounds,
    getNextNodePosition,
  };
}
